import { useImportFileMutation } from 'generated/graphql';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient, CombinedError } from 'urql';
import { GRAPHQL_UPLOAD_TYPE_HEADER, GraphQLUploadType } from 'enum/GraphQLUploadType';
import { modifyFetchOptionsHeaders } from 'utils/UrqlQueryModifier';
import { ImportFileErrorCode } from 'enum/ImportFileErrorCode';

export type AttachmentToUpload = File & { id: string };

export interface UseFilesForImportProps {
  refetchData: () => void;
  setIsUploadingFile: Dispatch<SetStateAction<boolean>>;
}

const MAX_FILE_SIZE: number = 104857600;

const EXTENSIONS_ICONS_IMPORT: Record<string, string> = {
  '.json': 'json-file'
};

export const useFilesForImport = (props: UseFilesForImportProps) => {
  const { refetchData, setIsUploadingFile } = props;
  const { t } = useTranslation();
  const client = useClient();

  const [toastMessage, setToastMessage] = useState('');
  const [toastOpen, setToastOpen] = useState(false);

  const [, uploadImportQT] = useImportFileMutation();

  const getErrorMessage = (combinedError?: CombinedError): string => {
    const errorCode = combinedError?.message.replace('[GraphQL] ', '');
    let message = '';
    switch (errorCode) {
      case ImportFileErrorCode.FILE_SIZE_EXCEEDED:
        message = t('import-error-file-size-exceeded');
        break;
      case ImportFileErrorCode.UPLOAD_ERROR:
        message = t('import-error-upload');
        break;
      case ImportFileErrorCode.PARSE_ERROR:
        message = t('import-error-failed-to-parse');
        break;
      case ImportFileErrorCode.INVALID_MIME_TYPE:
        message = t('import-error-invalid-mime-type', {
          'accepted-mimetypes': Object.keys(EXTENSIONS_ICONS_IMPORT).join(', ')
        });
        break;
      default:
        message = t('import-error-generic');
    }
    return message;
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setToastOpen(true);
  };

  const validateFile = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      showToastMessage(t('import-error-file-size-exceeded'));
      return false;
    }
    if (file.type !== 'application/json') {
      showToastMessage(
        t('import-error-invalid-mime-type', {
          'accepted-mimetypes': Object.keys(EXTENSIONS_ICONS_IMPORT).join(', ')
        })
      );
      return false;
    }
    return true;
  };

  const handleFileImport = async (event: CustomEvent) => {
    const { files } = event.target as any;
    if (files.length === 0) return;
    const file = files[0];
    if (validateFile(file) === false) return;

    setIsUploadingFile(true);
    const fetchOptions = modifyFetchOptionsHeaders(client.fetchOptions, {
      [GRAPHQL_UPLOAD_TYPE_HEADER]: GraphQLUploadType.QT_IMPORT
    });
    const result = await uploadImportQT({ file }, { fetchOptions });
    if (result.error) {
      showToastMessage(getErrorMessage(result.error));
      refetchData();
    } else {
      showToastMessage(t('manage-queries-import-query-upload-successful'));
    }
    setIsUploadingFile(false);
  };

  return {
    handleFileImport,
    toastMessage,
    toastOpen,
    setToastOpen
  };
};
